/**
 * Labstep
 *
 * @module models/log
 * @desc Typescript export class for Log
 */

import { Type } from 'class-transformer';
import type { EntityNameMap } from 'labstep-web/models';
import { Entity } from 'labstep-web/models/entity.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import type { PartialEntity } from 'labstep-web/models/partial-entity.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolTable } from 'labstep-web/models/protocol-table.model';
import { RequestTrace } from 'labstep-web/models/request-trace.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import { UAParser } from 'ua-parser-js';

export type LogObjectClass =
  | 'Device'
  | 'EntityState'
  | 'EntityStateWorkflow'
  | 'EntityView'
  | 'Experiment'
  | 'ExperimentWorkflow'
  | 'Group'
  | 'OrderRequest'
  | 'Organization'
  | 'Protocol'
  | 'ProtocolCollection'
  | 'PurchaseOrder'
  | 'Resource'
  | 'ResourceItem'
  | 'ResourceLocation'
  | 'Tag'
  | 'User';

export const LogObjectClassLabels = {
  Device: getHumanReadableEntityName('device', true, true),
  EntityState: getHumanReadableEntityName('entity_state', true, true),
  EntityStateWorkflow: getHumanReadableEntityName(
    'entity_state_workflow',
    true,
    true,
  ),
  EntityView: getHumanReadableEntityName('entity_view', true, true),
  Experiment: getHumanReadableEntityName('experiment', true, true),
  ExperimentWorkflow: getHumanReadableEntityName(
    'experiment_workflow',
    true,
    true,
  ),
  Group: getHumanReadableEntityName('group', true, true),
  OrderRequest: getHumanReadableEntityName(
    'order_request',
    true,
    true,
  ),
  Organization: getHumanReadableEntityName(
    'organization',
    true,
    true,
  ),
  Protocol: getHumanReadableEntityName('protocol', true, true),
  ProtocolCollection: getHumanReadableEntityName(
    'protocol_collection',
    true,
    true,
  ),
  PurchaseOrder: getHumanReadableEntityName(
    'purchase_order',
    true,
    true,
  ),
  Resource: getHumanReadableEntityName('resource', true, true),
  ResourceItem: getHumanReadableEntityName(
    'resource_item',
    true,
    true,
  ),
  ResourceLocation: getHumanReadableEntityName(
    'resource_location',
    true,
    true,
  ),
  Tag: getHumanReadableEntityName('tag', true, true),
  User: getHumanReadableEntityName('user', true, true),
};

export class Log extends Entity {
  static readonly entityName = 'log' as const;

  get entityName(): string {
    return Log.entityName;
  }

  constructor(data: Partial<Log> = {}) {
    super();
    Object.assign(this, data);
  }

  id!: string;

  created_at!: string;

  ended_at!: string;

  message!: string;

  action!: string;

  type!: string;

  previous_value!: unknown;

  new_value!: unknown;

  is_log_entry_snapshot_available!: boolean;

  object_id!: number;

  object_class!: string;

  log_entry_object_class!: keyof EntityNameMap;

  log_entry_object_id!: number;

  log_entry_client_user_agent!: string;

  log_entry_client_uuid!: string;

  loggable_parents: PartialEntity[] = [];

  @Type(() => RequestTrace)
  request_trace!: RequestTrace;

  @Type(() => User)
  author!: User;

  get objectClassToEntityName(): string {
    if (this.object_class === 'Protocol') {
      return Protocol.entityName;
    }
    if (this.object_class === 'Experiment') {
      return Experiment.entityName;
    }
    if (this.object_class === 'ProtocolTable') {
      return ProtocolTable.entityName;
    }
    return '';
  }

  get diff(): string | undefined {
    if (this.action === 'update') {
      return JSON.stringify({
        previous_value: this.previous_value,
        new_value: this.new_value,
      });
    }
    return undefined;
  }

  get clientIp(): string {
    return this.request_trace?.client_ip || '';
  }

  get client_user_agent(): string {
    return (
      this.log_entry_client_user_agent ||
      this.request_trace?.client_user_agent ||
      ''
    );
  }

  get userAgentObject(): UAParser.IResult {
    const parser = new UAParser();

    parser.setUA(this.client_user_agent);

    return parser.getResult();
  }

  get clientDisplayLabel(): string {
    return `${this.browser.name} ${this.printDeviceType} ${this.os.name}`;
  }

  get browser(): UAParser.IBrowser {
    return this.userAgentObject.browser;
  }

  get device(): UAParser.IDevice {
    return this.userAgentObject.device;
  }

  get os(): UAParser.IOS {
    return this.userAgentObject.os;
  }

  get printDeviceType(): string {
    const { device } = this.userAgentObject;
    return device.type || 'desktop';
  }
}
